import { Injectable } from '@angular/core';
import { StorageItem } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  /**
   * Set an item in localStorage with an optional time-to-live (TTL).
   * @param key - key under which the value will be stored
   * @param value - Value to be stored (string, number, boolean, or object)
   * @param ttlSeconds - Optional time-to-live in seconds. If provided, the item will expire after this time. If not provided, the item will persist indefinitely.
   */
  public setItem(
    key: string,
    value: string | number | boolean | object,
    ttlSeconds?: number
  ): void {
    if (typeof window !== 'undefined') {
      try {
        const storageItem: StorageItem<typeof value> = {
          value,
          expiresAt: ttlSeconds ? Date.now() + ttlSeconds * 1000 : null,
        };
        localStorage.setItem(key, JSON.stringify(storageItem));
      } catch (error) {
        console.error('Error saving to localStorage', error);
      }
    }
  }

  /**
   * Get an item from localStorage. If the item has expired, it will be removed and null will be returned.
   * @param key - key of the item to retrieve
   * @returns The value associated with the key, or null if the item does not exist or has expired.
   */
  public getItem<T>(key: string): T | null {
    if (typeof window !== 'undefined') {
      try {
        const item = localStorage.getItem(key);
        if (!item) return null;

        const storageItem: StorageItem<T> = JSON.parse(item);

        // Check if the item has an expiration and if it has expired
        if (storageItem.expiresAt && Date.now() > storageItem.expiresAt) {
          this.removeItem(key);
          return null;
        }

        return storageItem.value;
      } catch (error) {
        console.error('Error reading from localStorage', error);
        return null;
      }
    }
    return null;
  }

  public removeItem(key: string): void {
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error('Error removing from localStorage', error);
      }
    }
  }
  
  public removeAll(): void {
    if (typeof window !== 'undefined') {
      try {
        localStorage.clear();
      } catch (error) {
        console.error('Error removing from localStorage', error);
      }
    }
  }
}
